import 'react-toastify/dist/ReactToastify.css';
import React from 'react';
import {
  toast,
  ToastContainer,
  Slide,
  type ToastOptions,
} from 'react-toastify';
import { cva } from 'class-variance-authority';
import cn from '../utils/cn';
import { zIndex } from '../../z-index';

const snackVariants = cva(
  // Opaque tints (not the KoobAlert /5 alphas) — toasts float over content
  'relative flex items-center gap-3 overflow-hidden rounded-lg border border-solid p-4 mb-2 text-sm font-medium shadow-lg cursor-pointer',
  {
    variants: {
      type: {
        default: 'border-blue-600/20 bg-blue-50 text-blue-800',
        info: 'border-blue-600/20 bg-blue-50 text-blue-800',
        success: 'border-green-600/20 bg-green-50 text-green-800',
        warning: 'border-amber-600/20 bg-amber-50 text-amber-800',
        error: 'border-red-600/20 bg-red-50 text-red-800',
      },
    },
    defaultVariants: { type: 'default' },
  },
);

const typeIcons = {
  default: 'fa-circle-info',
  info: 'fa-circle-info',
  success: 'fa-circle-check',
  warning: 'fa-triangle-exclamation',
  error: 'fa-circle-exclamation',
};

const variantOf = (type?: string) =>
  (type && type in typeIcons ? type : 'default') as keyof typeof typeIcons;

export const notify = (
  message: React.ReactNode,
  options: ToastOptions = {},
) => {
  const type = variantOf(options.type ?? 'success');
  return toast(
    <>
      <i className={cn('fa-solid text-base', typeIcons[type])} />
      <div className="min-w-0 flex-1">{message}</div>
    </>,
    { ...options, type },
  );
};

export const notifyError = (
  message: React.ReactNode,
  options: ToastOptions = {},
) => notify(message, { type: 'error', ...options });

// toast() is a module-level singleton, so no context is needed; the hook is
// kept so the 200+ `useSnack()` call sites keep working unchanged.
const snackApi = { notify, notifyError };
export const useSnack = () => snackApi;

const CloseButton = ({ closeToast }: { closeToast?: () => void }) => (
  <button
    type="button"
    aria-label="close"
    onClick={closeToast}
    className="self-center border-none bg-transparent p-0 text-base text-current opacity-60 cursor-pointer hover:opacity-100"
  >
    <i className="fa-light fa-xmark" />
  </button>
);

const KoobSnackProvider = ({ children }: { children?: React.ReactNode }) => (
  <>
    {children}
    <ToastContainer
      position="bottom-center"
      transition={Slide}
      // Toasts are the top tier of the scale (packages/ui/z-index.js): they
      // must never be covered, including by a modal or its popovers.
      // width overrides the 320px default from ReactToastify.css (the
      // container centers itself with translateX(-50%))
      // min() so the inline width can't defeat the mobile (<480px)
      // full-width media query in ReactToastify.css
      style={{ width: 'min(420px, 100vw)', zIndex: zIndex.snack }}
      toastClassName={(ctx) => snackVariants({ type: variantOf(ctx?.type) })}
      bodyClassName={() => 'flex items-center gap-3 min-w-0 flex-1'}
      progressClassName={(ctx) =>
        cn(ctx?.defaultClassName, '!bg-current !opacity-10')
      }
      closeButton={CloseButton}
    />
  </>
);

export default KoobSnackProvider;
